import { inject, Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { AuthService } from '../auth-service/auth.service';
import { Project, Role } from '../../types/ProjectData';
import { USER_CHECK } from 'src/app/core/constants';
import { formatRoleName } from '../../utils/utility-functions';
import { Router } from '@angular/router';

@Injectable({
  providedIn: 'root',
})
export class RoleService {
  private customAuthService = inject(AuthService);
  projectDetails: Project = {} as Project;
  private router = inject(Router);
  private currentRoleSubject = new BehaviorSubject<{
    id: string;
    value: string;
  }>({ id: '', value: '' });
  currentRole$ = this.currentRoleSubject.asObservable();

  constructor() {}

  setCurrentRole(role: { id: string; value: string } | null) {
    if (!role) {
      return;
    }
    localStorage.setItem('currentRole', JSON.stringify(role));
  }

  getCurrentRole(): Role | null {
    const storedRole: Role = JSON.parse(localStorage.getItem('currentRole')!);
    if (storedRole) {
      const isRoleAvailables =
        this.customAuthService.roleSubject.value.includes(storedRole?.id);
      if (!isRoleAvailables) {
        return null;
      }
      return storedRole;
    } else {
      return null;
    }
  }

  clearRole() {
    this.currentRoleSubject.next({ id: '', value: '' });
  }

  isCurrentRoleSuperAdmin() {
    return this.getCurrentRole()
      ?.id.toLowerCase()
      .includes(USER_CHECK.SUPER_ADMIN);
  }

  isCurrentRoleProjectAdmin(projectCode: string) {
    return this.getCurrentRole()
      ?.id.toLowerCase()
      .includes(USER_CHECK.PROJECT_ADMIN(projectCode));
  }

  isUserProjectAdmin(projectCode: string) {
    const assignedRoles = this.getAssignedRoles();
    return assignedRoles.some((role) =>
      role.toLowerCase().includes(USER_CHECK.PROJECT_ADMIN(projectCode))
    );
  }

  isUserSuperAdmin() {
    const assignedRoles = this.getAssignedRoles();
    return assignedRoles.some((role) =>
      role.toLowerCase().includes(USER_CHECK.SUPER_ADMIN)
    );
  }

  isUserP1(projectCode: string) {
    const assignedRoles = this.getAssignedRoles();
    return assignedRoles.some((role) =>
      role.toLowerCase().includes(USER_CHECK.USER_P1(projectCode))
    );
  }

  isUserP2(projectCode: string) {
    const assignedRoles = this.getAssignedRoles();
    return assignedRoles.some((role) =>
      role.toLowerCase().includes(USER_CHECK.USER_P2(projectCode))
    );
  }

  getProjectAdminRole(projectCode: string): Role {
    const roleId = this.customAuthService.roleSubject.value.find((role) =>
      role.toLowerCase().includes(USER_CHECK.PROJECT_ADMIN(projectCode))
    )!;
    return {
      id: roleId,
      value: formatRoleName(roleId),
    };
  }

  getSuperAdminRole(): Role {
    const roleId = this.customAuthService.roleSubject.value.find((role) =>
      role.toLowerCase().includes(USER_CHECK.SUPER_ADMIN)
    )!;
    return {
      id: roleId,
      value: formatRoleName(roleId),
    };
  }

  setSuperAdminRole() {
    const superAdmin = this.getSuperAdminRole();
    if (superAdmin) {
      this.setCurrentRole(superAdmin);
    }
  }

  serProjectAdminRole(projectCode: string) {
    const projectAdmin = this.getProjectAdminRole(projectCode);
    if (projectAdmin) {
      this.setCurrentRole(projectAdmin);
    }
  }

  getP1UserRole(projectCode: string): Role {
    const roleId = this.customAuthService.roleSubject.value.find((role) =>
      role.toLowerCase().includes(USER_CHECK.USER_P1(projectCode))
    )!;
    return {
      id: roleId,
      value: formatRoleName(roleId),
    };
  }

  getP2UserRole(projectCode: string): Role {
    const roleId = this.customAuthService.roleSubject.value.find((role) =>
      role.toLowerCase().includes(USER_CHECK.USER_P2(projectCode))
    )!;
    return {
      id: roleId,
      value: formatRoleName(roleId),
    };
  }

  getAvailableRoles(projectCode: string): Role[] {
    const currentUrl = this.router.url;

    // If on /assign-user or /admin-wizard, return only Admin and Super Admin
    if (currentUrl === '/assign-user' || currentUrl.includes('/admin-wizard')) {
      const roles: Role[] = [];

      if (this.isUserProjectAdmin(projectCode)) {
        roles.push(this.getProjectAdminRole(projectCode));
      }

      if (this.isUserSuperAdmin()) {
        roles.push(this.getSuperAdminRole());
      }

      return roles;
    }

    if (currentUrl === '/assign-super-admin') {
      return [this.getSuperAdminRole()];
    }

    const availableRoles = this.customAuthService.roleSubject.value
      .filter((role) => role.toLowerCase().includes(projectCode.toLowerCase()))
      .map((role) => ({ id: role, value: formatRoleName(role) }));

    // if (this.isUserSuperAdmin()) {
    //   availableRoles.push(this.getSuperAdminRole());
    // }

    return availableRoles;
  }

  setPrecedenceRole(projectCode?: string) {
    const roles = this.customAuthService.roleSubject.value;
    // 1. Super Admin
    const superAdmin = roles.find((role) =>
      role.toLowerCase().includes(USER_CHECK.SUPER_ADMIN)
    );
    if (superAdmin) {
      this.setCurrentRole({
        id: superAdmin,
        value: formatRoleName(superAdmin),
      });
      return;
    }

    // If projectCode is provided
    if (projectCode) {
      this.setAdminAndUserPrecedence(projectCode);
      return;
    }
    this.setCurrentRole(null);
  }

  setAdminAndUserPrecedence(projectCode: string) {
    const roles = this.customAuthService.roleSubject.value;
    // If projectCode is provided
    if (projectCode) {
      // 2. Project Admin
      const projectAdmin = roles.find((role) =>
        role.toLowerCase().includes(USER_CHECK.PROJECT_ADMIN(projectCode))
      );
      if (projectAdmin) {
        this.setCurrentRole({
          id: projectAdmin,
          value: formatRoleName(projectAdmin),
        });
        return;
      }

      // 3. Fallback role with project code
      const fallback = roles.find((role) =>
        role.toLowerCase().includes(projectCode.toLowerCase())
      );
      if (fallback) {
        this.setCurrentRole({
          id: fallback,
          value: formatRoleName(fallback),
        });
        return;
      }
    }

    this.setCurrentRole(null);
  }
  
  getAssignedRoles() {
    return this.customAuthService.roleSubject.value;
  }
}
